import isArray from "lodash/isArray";

export function getQueryVariable(variable: string): string {
  const query = window.location.hash.substring(1);
  const vars = query.split("&");

  for (let i = 0; i < vars.length; i++) {
    const pairs = vars[i].split("=");
    if (decodeURIComponent(pairs[0]) === variable) {
      return decodeURIComponent(pairs[1]) as string;
    }
  }

  return "";
}

/**
 * The product a share widget was opened for, read from the iframe hash. Same
 * shape as fbt-attribution's mapProduct, so {{product.name}} matches the email.
 */
export function getSharedProductFromUrl() {
  const id = getQueryVariable("fbProductId");
  const sku = getQueryVariable("fbSku");
  const name = getQueryVariable("fbProductName");

  if (!id && !sku && !name) {
    return undefined;
  }

  return {
    id,
    variantId: getQueryVariable("fbProductVariantId"),
    sku,
    name,
    imageUrl: getQueryVariable("fbProductImageUrl"),
  };
}

/**
 * The `language-country` locale a host page's URL path starts with, lowercased
 * (`/en-de/refer-a-friend` -> `en-de`), or undefined when it starts with none.
 *
 * ENH-359: On Running's site serves every market under such a prefix, and it is
 * the locale their mailing list mutation needs to pick the right market.
 */
export function getLocaleFromPathname(
  pathname: string | undefined
): string | undefined {
  const match = /^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i.exec(pathname || "");
  return match ? match[1].toLowerCase() : undefined;
}

type UrlQueryValue = string | boolean | number;

interface ConvertToUrlQueryParams {
  [variable: string]: UrlQueryValue | UrlQueryValue[];
}

export function convertToUrlQuery(params: ConvertToUrlQueryParams): string {
  const queries: string[] = [];

  for (const key in params) {
    // cast value into an array
    const values = isArray(params[key]) ? params[key] : [params[key]];

    for (const value of values as UrlQueryValue[]) {
      queries.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
    }
  }

  return queries.join("&");
}

/**
 * The params fbt-attribution's redirect appends on every hop
 * (`buildDestinationUrl`). They are ours, and a second copy with a different
 * value is never meaningful.
 *
 * `utm_*` and `cid` are deliberately absent: a utm already on the destination
 * URL wins over the share's own blob, and a cid may be the merchant's.
 */
const FRIENDBUY_QUERY_PARAMS = [
  "fbuy",
  "fbshare",
  "referralCode",
  "fbuy_ref_code",
];

/** Split on the first `separator` only; the tail keeps any further ones. */
function splitOnce(value: string, separator: string): [string, string?] {
  const at = value.indexOf(separator);

  return at === -1
    ? [value, undefined]
    : [value.slice(0, at), value.slice(at + 1)];
}

/**
 * FBE-6972: a friend who arrives through a product-share link still has our
 * params in the address bar, so a share generated from that page carried them
 * into the next destination URL and the redirect appended a second, conflicting
 * set -- two `fbuy`, two `referralCode`, two `fbshare`, and a URL long enough
 * to 502. Drop ours so the redirect's own set is the only one.
 *
 * Mirrors `parseShareUtms`, which already drops landing-page `utm_*` for this
 * same collision.
 */
export function stripFriendbuyQueryParams(url: string): string {
  if (!url) {
    // A share can be taken from a widget that never got a current url.
    return url;
  }

  const [base, search] = splitOnce(url, "?");

  if (!search) {
    return url;
  }

  const [query, hash] = splitOnce(search, "#");
  const pairs = query.split("&");
  const kept = pairs.filter(
    (pair) => !FRIENDBUY_QUERY_PARAMS.includes(pair.split("=")[0])
  );

  if (kept.length === pairs.length) {
    // Nothing of ours: hand back the merchant's URL exactly as it came, rather
    // than re-encoding their query by rebuilding it.
    return url;
  }

  const rest = hash === undefined ? "" : `#${hash}`;

  return kept.length ? `${base}?${kept.join("&")}${rest}` : `${base}${rest}`;
}
